import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} ConfirmStripePaymentData
 * @property {number} invoice_id - Invoice ID
 * @property {string} payment_intent - Stripe payment intent ID from callback
 * @property {string|number} amount - Payment amount
 * @property {string} currency - Currency code (e.g., "USD", "INR")
 */

/**
 * @description Custom hook to confirm Stripe payment after successful transaction
 */
export const useConfirmStripePayment = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const { axiosInstance } = useAxios();

    /**
     * @description Confirm Stripe payment
     * @async
     * @param {ConfirmStripePaymentData} paymentData - The payment confirmation data
     * @param {() => void} [onSuccess] - Callback function to be called on success
     * @returns {Promise<ConfirmPaymentResponse | null>} Returns response on success, null on failure
     */
    const confirmPayment = async (paymentData, onSuccess) => {
        try {
            setLoading(true);
            setError("");
            setSuccess(false);

            const response = await axiosInstance.post(
                `${TENANT_DOMAIN}/billing/payments/online/stripe/confirm`,
                {
                    invoice_id: paymentData.invoice_id,
                    payment_intent: paymentData.payment_intent,
                    amount: String(paymentData.amount),
                    currency: paymentData.currency,
                }
            );

            const data = response.data;
            setSuccess(true);

            if (onSuccess) onSuccess(data);

            return data;
        } catch (err) {
            setSuccess(false);

            if (err?.response?.status === 500 || err?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                const apiErrors = err?.response?.data?.errors;
                const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];
                setError(firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }

            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reset the hook state
     */
    const reset = () => {
        setLoading(false);
        setError("");
        setSuccess(false);
    };

    return {
        loading,
        error,
        success,
        confirmPayment,
        reset,
    };
};

export default useConfirmStripePayment;